/**
 * The race behind the title, without its drawing: the endless course, the autopilot that runs it,
 * and the pacing that keeps two runners trading the lead. Pure simulation on top of `@lache/sim`,
 * so a headless script can play it for minutes and count the falls (the autopilot must clear the
 * course it generates). The course is generated once from a fixed seed out of the game's own
 * obstacles (gaps, lanes of bars over water, boxes to vault, pipes to slide under, hurdles), laid
 * out as one period repeated three times; when the leader has run a period past the middle one,
 * every runner shifts back by a period onto the same layout and the race never ends.
 */

import {
  type CourseData,
  type CourseEntity,
  createInputFrame,
  type InputFrame,
  S,
  Sim,
  type SimState,
  SolidClass,
  type Tuning,
} from "@lache/sim";
import { Rng } from "./rng";

/** The layout repeats this many times; runners wrap back a period once past the middle one. */
const PERIODS = 3;
/** Where the runners jump for a bar (from a full run) and for a plain gap, m before it. */
const BAR_JUMP_DISTANCE = 3.8;
const GAP_TAKEOFF = 0.8;
/** The swing angle past the bottom for the kick release, rad (the owner's lane tape lets go past 40 degrees). */
const RELEASE_ANGLE = 0.7;
/** Seconds before the bottom of the swing to tuck (shortening the pendulum through the bottom speeds it up). */
const TUCK_BEFORE_BOTTOM_S = 0.045;
/** Ticks stopped against something with the run held before the autopilot hops over it. */
const STUCK_TICKS = 30;
/** Runners this far apart or more rubber-band: the one behind flat out, the one ahead easing off. */
const RUBBER_BAND_GAP = 5;

type FeatureKind = "flat" | "gap" | "lane" | "vault" | "slide" | "hurdle";

interface Feature {
  kind: FeatureKind;
  /** Length of the obstacle itself, m (water width, box width). */
  width: number;
  bars: number;
  height: number;
}

interface GeneratedCourse {
  course: CourseData;
  period: number;
  respawnsPerPeriod: number;
}

/** Flat ground before and after every obstacle, m. */
const LEAD = 8;
const TAIL = 4;

/** One period of obstacles from the seed, repeated `PERIODS` times, with a respawn point before each. */
export function generateCourse(seed: number): GeneratedCourse {
  const rng = new Rng(seed);
  const features: Feature[] = [{ kind: "flat", width: 6, bars: 0, height: 0 }];
  let last: FeatureKind = "flat";
  for (let i = 0; i < 9; i++) {
    let kind: FeatureKind = last;
    while (kind === last) {
      kind = rng.pick<FeatureKind>([
        { weight: 3, value: "gap" },
        { weight: 4, value: "lane" },
        { weight: 3, value: "vault" },
        { weight: 2, value: "slide" },
        { weight: 1, value: "hurdle" },
      ]);
    }
    last = kind;
    const bars = kind === "lane" ? rng.int(2, 4) : 0;
    const width =
      kind === "gap"
        ? rng.range(2.6, 3.3)
        : kind === "lane"
          ? 3 * bars + 3
          : kind === "vault"
            ? 1
            : kind === "slide"
              ? 2
              : 0.8;
    const height = kind === "vault" ? (rng.next() < 0.5 ? 1 : 1.2) : kind === "hurdle" ? 0.4 : 0;
    features.push({ kind, width, bars, height });
  }
  let period = 0;
  for (const f of features) period += LEAD + f.width + TAIL;
  const entities: CourseEntity[] = [];
  const respawns: { x: number; y: number }[] = [];
  // The ground is one solid per stretch between waters.
  let groundStart = -3;
  const water = (x0: number, w: number): void => {
    entities.push({ type: "solid", x: groundStart, y: -1, w: x0 - groundStart, h: 1 });
    entities.push({ type: "water", x: x0, y: -2, w, h: 1 });
    groundStart = x0 + w;
  };
  for (let k = 0; k < PERIODS; k++) {
    let x = k * period;
    for (const f of features) {
      respawns.push({ x: x + 2, y: 0 });
      const at = x + LEAD;
      switch (f.kind) {
        case "gap":
          water(at, f.width);
          break;
        case "lane":
          water(at, f.width);
          for (let b = 0; b < f.bars; b++) entities.push({ type: "bar", x: at + 3 + 3 * b, y: 3.8 });
          break;
        case "vault":
        case "hurdle":
          entities.push({ type: "box", x: at, y: 0, w: f.width, h: f.height });
          break;
        case "slide":
          entities.push({ type: "pipe", x: at, y: 1.5, w: f.width, h: 0.4 });
          break;
        case "flat":
          break;
      }
      x += LEAD + f.width + TAIL;
    }
  }
  const end = PERIODS * period + 20;
  entities.push({ type: "solid", x: groundStart, y: -1, w: end - groundStart, h: 1 });
  entities.push({ type: "solid", x: -4, y: -1, w: 1, h: 6 });
  return {
    course: { version: 1, name: "attract", start: { x: 2, y: 0 }, respawns, marks: [], entities },
    period,
    respawnsPerPeriod: features.length,
  };
}

/** Reads the world ahead of one runner and presses what the course asks for. */
export class Autopilot {
  readonly frame: InputFrame = createInputFrame();
  private jumpHold = 0;
  private reachHeld = false;
  /** Tucking through the bottom of the swing. */
  private swingTuck = false;
  private stuckTicks = 0;
  private slidePipe = -1;
  private vaultBox = -1;
  private vaultLeft = 0;

  constructor(readonly sim: Sim) {}

  /** Fill the frame for this tick; `pace` is the run input on open ground (1 is flat out). */
  tick(pace: number): void {
    const s = this.sim.state;
    const w = this.sim.world;
    const t = this.sim.tuning;
    const f = this.frame;
    f.moveX = pace;
    f.jump = false;
    f.tuck = false;
    f.reach = 0;
    f.tiltX = 0;
    if (s.barId >= 0) {
      // On a bar, the owner's lane recipe: lean with the swing, tuck through the bottom, and kick off
      // past the release angle on a forward swing that has crossed the bottom, diving at the next bar.
      f.moveX = 0;
      f.reach = 1;
      f.tiltX = s.omega >= 0 ? 1 : -1;
      const approaching = s.theta * s.omega < 0;
      const toBottom = s.omega !== 0 ? Math.abs(s.theta / s.omega) : 1e9;
      if (approaching && toBottom < TUCK_BEFORE_BOTTOM_S) this.swingTuck = true;
      if (this.swingTuck && !approaching && Math.abs(s.theta) > 0.15) this.swingTuck = false;
      f.tuck = this.swingTuck;
      const bx = w.barX[s.barId]!;
      let next = false;
      for (let i = 0; i < w.barCount; i++) {
        const d = w.barX[i]! - bx;
        if (d > 0.5 && d < 3.6) next = true;
      }
      if (s.omega > 0 && s.theta > RELEASE_ANGLE && s.lastCrossTime > -1e8) {
        f.jump = true;
        f.moveX = 1;
        f.tuck = false;
        this.swingTuck = false;
        this.reachHeld = next;
        this.jumpHold = 0;
      }
      return;
    }
    if (s.grounded !== 1) {
      // In the air: a jump stays held for its full height; toward a bar the arms stay out and the body
      // dives; out of the last bar the body tilts forward, then tucks, to roll out of the drop.
      f.moveX = 1;
      if (this.jumpHold > 0) {
        f.jump = true;
        this.jumpHold--;
      }
      f.reach = this.reachHeld ? 1 : 0;
      if (this.reachHeld) f.tiltX = 1;
      return;
    }
    this.reachHeld = false;
    this.swingTuck = false;
    // Stopped against something (a missed vault): hop over it rather than run at it for ever.
    this.stuckTicks = Math.abs(s.vx) < 0.2 && s.stateTicks > 6 ? this.stuckTicks + 1 : 0;
    if (this.stuckTicks > STUCK_TICKS) {
      this.stuckTicks = 0;
      this.vaultBox = -1;
      f.jump = true;
      this.jumpHold = 18;
      return;
    }
    const x = s.x;
    // Water ahead: a lane of bars wants a dive at the first bar, a plain gap a jump from the edge.
    let water = -1;
    let wx0 = Number.POSITIVE_INFINITY;
    for (let i = 0; i < w.waterCount; i++) {
      const x0 = w.waterX0[i]!;
      if (x0 >= x - 1 && x0 - x < 14 && x0 < wx0) {
        water = i;
        wx0 = x0;
      }
    }
    if (water >= 0) {
      f.moveX = 1;
      const wx1 = w.waterX1[water]!;
      let bar = Number.POSITIVE_INFINITY;
      for (let i = 0; i < w.barCount; i++) {
        const bx = w.barX[i]!;
        if (bx > wx0 && bx < wx1 && bx < bar) bar = bx;
      }
      if (bar < Number.POSITIVE_INFINITY) {
        if (x >= bar - BAR_JUMP_DISTANCE) {
          f.jump = true;
          f.reach = 1;
          this.jumpHold = 40;
          this.reachHeld = true;
          return;
        }
      } else if (x >= wx0 - GAP_TAKEOFF) {
        f.jump = true;
        this.jumpHold = 16;
        return;
      }
    }
    // A pipe: slide under it, from a couple of meters before until past it.
    if (this.slidePipe >= 0) {
      if (x > w.solidX1[this.slidePipe]! + 0.6) this.slidePipe = -1;
      else {
        f.moveX = 1;
        f.tuck = true;
        return;
      }
    }
    const pipe = w.nextObstacleAhead(x, s.y, t.capsuleRadius, t.standHeight, 1, SolidClass.Pipe, 2.4);
    if (pipe >= 0) {
      this.slidePipe = pipe;
      f.moveX = 1;
      f.tuck = true;
      return;
    }
    // A box: the vault is on the button, tapped just before the takeoff point (as video.ts does).
    if (this.vaultLeft > 0) {
      this.vaultLeft--;
      f.tuck = this.vaultLeft > 0;
      f.moveX = 1;
      return;
    }
    const speed = Math.abs(s.vx);
    // Only a run can vault: a roll out of a landing would keep the press for the box.
    if (speed < 0.5 || s.state !== S.Run) return;
    const box = w.nextObstacleAhead(
      x,
      s.y,
      t.capsuleRadius,
      t.standHeight,
      1,
      SolidClass.Box,
      t.vaultLookahead,
    );
    if (box < 0 || box === this.vaultBox) return;
    const h = w.solidY1[box]! - s.y;
    if (h <= t.hurdleStepHeight || h > t.hurdleMaxHeight) return;
    f.moveX = 1;
    const com = t.standHeight / 2 - t.tuckComDrop * s.tuck;
    const rise = Math.max(h + t.vaultHipClearance - com, t.vaultHipClearance);
    const lead = w.queryDist / speed - Math.sqrt((2 * rise) / t.gravity);
    if (lead <= 0.06) {
      this.vaultBox = box;
      this.vaultLeft = 3;
      f.tuck = true;
    }
  }

  /** The runner moved back a period: forget the obstacles it remembered by index. */
  wrapped(): void {
    this.slidePipe = -1;
    this.vaultBox = -1;
  }
}

export interface RaceRunner {
  sim: Sim;
  pilot: Autopilot;
  /** The runner's form cycles on this many radians per tick, offset by `phase`, so the two differ. */
  period: number;
  phase: number;
  /** Falls into the water so far. */
  falls: number;
}

/** A runner may shift back a period only on its feet between obstacles. */
function safeToWrap(s: SimState): boolean {
  return s.grounded === 1 && s.barId < 0 && (s.state === S.Run || s.state === S.Idle);
}

/** Two autopilots on the endless course, ticked together, the one behind pushing harder. */
export class RaceSim {
  readonly runners: RaceRunner[] = [];
  readonly period: number;
  private readonly respawnsPerPeriod: number;
  /** How far the runners have been shifted back so far, m (a camera subtracts it too). */
  wraps = 0;
  private tick = 0;

  constructor(tuning: Tuning, seed = 20260920) {
    const generated = generateCourse(seed);
    this.period = generated.period;
    this.respawnsPerPeriod = generated.respawnsPerPeriod;
    // Form periods of about 23 and 31 seconds.
    const forms = [
      { period: (2 * Math.PI) / (23 * 120), phase: 0.4 },
      { period: (2 * Math.PI) / (31 * 120), phase: 3.5 },
    ];
    forms.forEach(({ period, phase }, i) => {
      const sim = new Sim(generated.course, tuning);
      sim.state.x -= 1.5 * i;
      sim.prev.x = sim.state.x;
      this.runners.push({ sim, pilot: new Autopilot(sim), period, phase, falls: 0 });
    });
  }

  get world() {
    return this.runners[0]!.sim.world;
  }

  /** One tick for every runner; true when they were shifted back a period this tick. */
  step(): boolean {
    this.tick++;
    const a = this.runners[0]!;
    const b = this.runners[1]!;
    const gap = Math.abs(a.sim.state.x - b.sim.state.x);
    for (const r of this.runners) {
      const other = r === a ? b : a;
      const behind = r.sim.state.x < other.sim.state.x;
      // Each runner's form rises and falls on its own slow period, so the lead changes hands every
      // half minute or so; past a few meters apart the one behind pushes and the one ahead eases off.
      let pace = 0.87 + 0.13 * Math.sin(this.tick * r.period + r.phase);
      if (gap > RUBBER_BAND_GAP) pace = behind ? 1 : Math.min(pace, 0.78);
      r.pilot.tick(Math.max(0.72, Math.min(1, pace)));
      const wasFailing = r.sim.state.state === S.Fail;
      r.sim.step(r.pilot.frame);
      r.sim.events.clear();
      if (!wasFailing && r.sim.state.state === S.Fail) r.falls++;
    }
    // Past the middle period: everyone moves back one, onto the same layout.
    const leadX = Math.max(a.sim.state.x, b.sim.state.x);
    if (leadX < 2 * this.period || !this.runners.every((r) => safeToWrap(r.sim.state))) return false;
    for (const r of this.runners) {
      const s = r.sim.state;
      s.x -= this.period;
      s.respawnIndex = Math.max(0, s.respawnIndex - this.respawnsPerPeriod);
      s.lastBarId = -1;
      s.plantBox = -1;
      s.vaultArmBox = -1;
      r.pilot.wrapped();
    }
    this.wraps++;
    return true;
  }
}
